import { useEffect, useState, type CSSProperties, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { Button } from '../ui/button';
import AppLogo from '../AppLogo';
import SafeText from '../SafeText';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getAuthSafe } from '../../lib/firebase';
import {
  LayoutDashboard,
  Search,
  Briefcase,
  MessageSquare,
  Menu,
  Users,
  Bell,
  Building2,
  ClipboardList,
  UserCheck,
  Star,
  LogOut,
  Settings,
  TrendingUp,
  BarChart3,
  CalendarDays,
  UserPlus,
} from 'lucide-react';
import { useUnreadCounts } from '../../context/UnreadCountsContext';
import { useAuthCtx } from '../../context/AuthContext';
import { ensureEmployerWallet } from '../../lib/wallets/ensureEmployerWallet';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import NavSettingsButton from '../NavSettingsButton';
import OverflowNav from '../nav/OverflowNav';

const EMPLOYER_WORKFORCE_PATH = '/employer/workforce';

const NAV_LINK_CLASS =
  'flex items-center gap-1 rounded-lg text-[13px] font-medium shrink-0 whitespace-nowrap';

const NAV_LINK_PADDING: CSSProperties = { padding: '6px 10px' };

export default function EmployerNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const unreadCounts = useUnreadCounts();
  const msgBadge =
    unreadCounts.hydrated && unreadCounts.messagesBadgeLive ? unreadCounts.messages : 0;
  const notifBadge =
    unreadCounts.hydrated && unreadCounts.notificationsBadgeLive ? unreadCounts.notifications : 0;
  const appBadge = unreadCounts.hydrated ? unreadCounts.applications : 0;
  const { user, profile } = useAuthCtx();
  const displayName = (profile && 'firstName' in profile)
    ? `${(profile as any).firstName || ''} ${(profile as any).lastName || ''}`.trim() || user?.email || 'Account'
    : user?.displayName || user?.email || 'Account';
  const avatarUrl = (profile as any)?.photoURL || user?.photoURL || '';

  useEffect(() => {
    if (!user?.uid) return;
    if (profile?.role !== 'employer') return;
    ensureEmployerWallet(user.uid).catch(() => {});
  }, [user?.uid, profile?.role]);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);

  const navButtonStyle = (path: string): CSSProperties => {
    if (isActive(path)) {
      return { color: 'var(--text-brand)', background: 'var(--rr-red-light)', fontWeight: 600, transition: 'all 0.15s ease' };
    }
    if (hoveredNav === path) {
      return { color: 'var(--text-brand)', background: 'var(--rr-red-light)', transition: 'all 0.15s ease' };
    }
    return { color: 'var(--text-secondary)', transition: 'all 0.15s ease' };
  };

  const iconOnlyStyle = (path: string): CSSProperties => {
    const active = isActive(path);
    const hovered = hoveredNav === path;
    return {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: active || hovered ? 'var(--text-brand)' : 'var(--text-muted)',
      background: active || hovered ? 'var(--rr-red-light)' : 'transparent',
      cursor: 'pointer',
      transition: 'all 0.15s ease',
    };
  };

  const navItems = [
    { to: '/employer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/employer/jobs', label: 'Jobs', icon: Briefcase },
    { to: '/employer/search', label: 'Search', icon: Search },
    { to: EMPLOYER_WORKFORCE_PATH, label: 'Workforce', icon: UserCheck, badge: appBadge },
    { to: '/employer/scheduling', label: 'Scheduling', icon: CalendarDays },
    { to: '/employer/team-onboarding', label: 'Orientation', icon: UserPlus },
    { to: '/employer/credentials', label: 'Credentials', icon: ClipboardList },
    { to: '/employer/insights', label: 'Insights', icon: TrendingUp },
    { to: '/employer/job-benchmarks', label: 'Job post benchmarks', icon: BarChart3 },
    { to: '/community', label: 'Community', icon: Users },
    { to: '/employer/feedback', label: 'Feedback', icon: Star },
    { to: '/employer/notifications', label: 'Notifications', icon: Bell, badge: notifBadge },
    { to: '/employer/messages', label: 'Messages', icon: MessageSquare, badge: msgBadge },
    { to: '/employer/company-profile', label: 'Company Profile', icon: Building2 },
    { to: '/employer/company-checklist', label: 'Hiring Checklist', icon: ClipboardList },
  ];

  // Top bar, in priority order: the first ones stay when the bar is narrow, the rest go into More.
  const desktopNavItems: Array<{ to: string; label: string; icon: typeof LayoutDashboard; badge?: number; testId?: string }> = [
    { to: '/employer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/employer/jobs', label: 'Jobs', icon: Briefcase },
    { to: '/employer/search', label: 'Search', icon: Search },
    { to: EMPLOYER_WORKFORCE_PATH, label: 'Workforce', icon: UserCheck, badge: appBadge },
    { to: '/employer/scheduling', label: 'Scheduling', icon: CalendarDays },
    { to: '/employer/team-onboarding', label: 'Orientation', icon: UserPlus },
    { to: '/employer/insights', label: 'Insights', icon: TrendingUp, testId: 'nav-insights' },
    { to: '/community', label: 'Community', icon: Users },
    { to: '/employer/feedback', label: 'Feedback', icon: Star },
  ];

  async function handleSignOut() {
    try {
      await signOut(getAuthSafe());
      navigate('/');
    } catch (err) {
      console.warn('[EmployerNav] Logout failed:', err);
    }
  }

  return (
    <div
      className="w-full flex items-center justify-between px-4 sm:px-6 bg-[var(--surface-nav)]"
      style={{ height: '64px', borderBottom: '1px solid var(--border-default)', position: 'sticky', top: 0, zIndex: 50 }}
    >
      <div className="max-w-[1920px] min-w-0 w-full mx-auto flex items-center justify-between gap-2 sm:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-4 md:pr-2">
            <div className="flex h-9 shrink-0 items-center">
              <AppLogo clickable={true} />
            </div>

            {/* The links get the whole bar up to 1920px. Each one is measured: the ones that fit stay in
                order and the rest move into a "More" menu, so a link is never cut off. Notifications and
                Messages live with Settings on the right, always visible. */}
            <OverflowNav
              ariaLabel="Employer"
              className="hidden min-h-0 min-w-0 flex-1 items-center gap-0.5 py-0.5 lg:flex overflow-hidden"
              isActive={isActive}
              remeasureKey={`${location.pathname}|${appBadge}`}
              moreStyle={(active) => (active ? { color: 'var(--text-brand)', background: 'var(--rr-red-light)', fontWeight: 600 } : { color: 'var(--text-secondary)' })}
              items={desktopNavItems.map(({ to, label, icon: Icon, badge, testId }) => ({
                to,
                label,
                badge,
                node: (
                  <Button
                    asChild
                    variant="ghost"
                    className={NAV_LINK_CLASS}
                    style={{ ...NAV_LINK_PADDING, ...navButtonStyle(to) }}
                    onMouseEnter={() => setHoveredNav(to)}
                    onMouseLeave={() => setHoveredNav(null)}
                    data-testid={testId}
                  >
                    <Link to={to}>
                      <Icon className="hidden w-4 h-4 2xl:inline" />
                      {label}
                      {badge != null && badge > 0 && (
                        <Badge className="ml-1 bg-[var(--rr-red)] text-white text-xs">
                          {badge}
                        </Badge>
                      )}
                    </Link>
                  </Button>
                ),
              }))}
            />

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="ml-auto shrink-0 lg:hidden h-11 w-11 rounded-lg min-h-11 min-w-11" aria-label="Open menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85vw] max-w-[320px] flex flex-col p-0">
                <SheetHeader className="p-4 border-b border-[var(--border-default)]">
                  <SheetTitle className="text-left">Menu</SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col p-2 flex-1 overflow-y-auto">
                  {navItems.map(({ to, label, icon: Icon, badge }) => (
                    <Button
                      key={to}
                      asChild
                      variant="ghost"
                      className={`w-full justify-start ${isActive(to) ? 'rr-nav-link--active' : ''} h-11 px-4`}
                    >
                      <Link to={to} onClick={() => setMobileMenuOpen(false)}>
                        {Icon && <Icon className="w-4 h-4 mr-3" />}
                        {label}
                        {badge != null && badge > 0 && (
                          <Badge className="ml-auto bg-[var(--rr-red)] text-white text-xs">
                            {badge > 99 ? '99+' : badge}
                          </Badge>
                        )}
                      </Link>
                    </Button>
                    ))}
                    <Button asChild variant="ghost" className={`w-full justify-start ${isActive('/employer/settings') ? 'rr-nav-link--active' : ''} h-11 px-4`}>
                      <Link to="/employer/settings" onClick={() => setMobileMenuOpen(false)}>
                        <Settings className="w-4 h-4 mr-3" />
                        Settings
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start h-11 px-4 text-[var(--text-brand)] hover:text-[var(--rr-red-hover)] hover:bg-[var(--rr-red-light)] mt-2"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        void handleSignOut();
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Log out
                    </Button>
                  </nav>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-1 border-l border-[var(--border-default)] pl-2 sm:pl-3 sm:gap-2" data-testid="employer-nav-actions">
            {/* lg and up only: below that both are in the menu, as before. */}
            <div className="hidden items-center gap-1 lg:flex">
              <Button
                asChild
                variant="ghost"
                size="icon"
                title="Notifications"
                aria-label="Notifications"
                className="relative flex items-center justify-center rounded-lg transition-all duration-150 text-[var(--text-muted)] hover:text-[var(--text-brand)] hover:bg-[var(--surface-hover)] shrink-0 border-0 shadow-none"
                style={{ width: '36px', height: '36px', ...(isActive('/employer/notifications') ? { color: 'var(--text-brand)', background: 'var(--rr-red-light)' } : {}) }}
              >
                <Link
                  to="/employer/notifications"
                  onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                    if (location.pathname === '/employer/notifications') e.preventDefault();
                  }}
                >
                  <Bell className="h-4 w-4" />
                  <span className="sr-only">Notifications</span>
                  {notifBadge > 0 && (
                    <Badge className="absolute -right-1 -top-1 h-5 min-w-[1.25rem] justify-center rounded-full bg-rose-600 px-1 text-[10px] text-white">
                      {notifBadge > 99 ? '99+' : notifBadge}
                    </Badge>
                  )}
                </Link>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="icon"
                title="Messages"
                aria-label="Messages"
                className="relative flex items-center justify-center rounded-lg transition-all duration-150 text-[var(--text-muted)] hover:text-[var(--text-brand)] hover:bg-[var(--surface-hover)] shrink-0 border-0 shadow-none"
                style={{ width: '36px', height: '36px', ...(isActive('/employer/messages') ? { color: 'var(--text-brand)', background: 'var(--rr-red-light)' } : {}) }}
              >
                <Link to="/employer/messages">
                  <MessageSquare className="h-4 w-4" />
                  <span className="sr-only">Messages</span>
                  {msgBadge > 0 && (
                    <Badge className="absolute -right-1 -top-1 h-5 min-w-[1.25rem] justify-center rounded-full bg-rose-600 px-1 text-[10px] text-white">
                      {msgBadge > 99 ? '99+' : msgBadge}
                    </Badge>
                  )}
                </Link>
              </Button>
            </div>
            <NavSettingsButton to="/employer/settings" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex max-w-[11rem] min-w-0 shrink-0 items-center gap-2 overflow-hidden rounded-lg px-2 py-2 text-left transition-colors hover:bg-[var(--surface-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--rr-red)] focus:ring-offset-2 sm:max-w-[12rem] sm:gap-3 sm:px-3 2xl:max-w-[14rem]"
                  aria-label="Account menu"
                >
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarImage src={avatarUrl} />
                    <AvatarFallback
                      className="flex items-center justify-center text-white font-bold text-sm rounded-full flex-shrink-0 border-0"
                      style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, var(--rr-red), var(--rr-brand))', boxShadow: '0 2px 8px color-mix(in srgb, var(--rr-red) 30%, transparent)' }}
                    >
                      {(displayName || 'A').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden min-w-0 flex-1 flex-col justify-center gap-0.5 overflow-hidden text-left leading-tight lg:flex">
                    <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                      <SafeText value={displayName} label="displayName" />
                    </p>
                    <p className="truncate text-xs text-[var(--text-muted)]">Account</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/employer/account">Account</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/employer/settings">Settings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="2xl:hidden">
                  <Link to="/employer/feedback">Feedback</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void handleSignOut()} className="text-rose-600 focus:text-rose-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </div>
  );
}
